import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '../../shared/components/AppComponents';
import { apiFetchJson } from '../../apiClient';
import { clean, createStrengthApi } from './strengthApi';
import { errorText, readStravaReturn } from './strengthFormat';
import TrainView from './TrainView';
import PlansView from './PlansView';
import SessionHistory from './SessionHistory';
import ProgressView from './ProgressView';
import StravaPanel from './StravaPanel';
import './StrengthApp.css';

function StrengthApp({ user }) {
  const { instance } = useMsal();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const api = useMemo(() => createStrengthApi(instance, i18n.language), [instance, i18n.language]);

  const [activeTab, setActiveTab] = useState('train');
  const [catalog, setCatalog] = useState([]);
  const [plans, setPlans] = useState([]);
  const [members, setMembers] = useState([]);
  const [stravaStatus, setStravaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const stravaReturnHandled = useRef(false);

  const showMessage = useCallback((text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  }, []);

  const loadPlans = useCallback(async () => {
    try {
      setPlans(await api.plans() || []);
    } catch (e) {
      showMessage(errorText(e, t('strength.errors.load')), 'error');
    }
  }, [api, showMessage, t]);

  const loadStravaStatus = useCallback(async () => {
    try {
      setStravaStatus(await api.stravaStatus());
    } catch (e) {
      console.error('Failed to load Strava status:', e);
    }
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [exercises, householdData] = await Promise.all([
          api.exercises(),
          apiFetchJson(instance, '/api/household-groups').then(clean).catch(() => [])
        ]);
        if (cancelled) return;
        setCatalog(exercises || []);
        // A user can be in several households; the filters want one de-duplicated list of people.
        const byId = new Map();
        (householdData || []).forEach(h => (h.members || []).forEach(m => {
          if (m.userId && !byId.has(m.userId)) byId.set(m.userId, { userId: m.userId, name: m.userName || m.userEmail || '?' });
        }));
        setMembers([...byId.values()]);
      } catch (e) {
        if (!cancelled) showMessage(errorText(e, t('strength.errors.load')), 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    loadPlans();
    loadStravaStatus();
    return () => { cancelled = true; };
  }, [api, instance, loadPlans, loadStravaStatus, showMessage, t]);

  // Strava redirects back here with ?code&state&scope. The code is single-use, so the
  // ref guards against React running the effect twice.
  useEffect(() => {
    const result = readStravaReturn(location.search);
    if (!result || stravaReturnHandled.current) return;
    stravaReturnHandled.current = true;
    setActiveTab('strava');
    navigate(location.pathname, { replace: true });

    if (result.error) {
      showMessage(t('strength.strava.denied'), 'error');
      return;
    }
    (async () => {
      try {
        setStravaStatus(await api.stravaExchange(result));
        showMessage(t('strength.strava.connected'));
      } catch (e) {
        showMessage(errorText(e, t('strength.strava.connectFailed')), 'error');
      }
    })();
  }, [api, location.pathname, location.search, navigate, showMessage, t]);

  const catalogByKey = useMemo(() => Object.fromEntries(catalog.map(e => [e.key, e])), [catalog]);

  const tabs = [
    { id: 'train', label: t('strength.tabs.train'), icon: '🏋️' },
    { id: 'plans', label: t('strength.tabs.plans'), icon: '📋' },
    { id: 'history', label: t('strength.tabs.history'), icon: '🗓️' },
    { id: 'progress', label: t('strength.tabs.progress'), icon: '📈' },
    { id: 'strava', label: t('strength.tabs.strava'), icon: '🔗' }
  ];

  const shared = { api, t, lang: api.language, showMessage, catalog, catalogByKey, members, user };

  return (
    <div className="st-app">
      <AppHeader title={t('strength.title')} subtitle={t('strength.subtitle')} user={user} />

      {message && (
        <div className={`st-message st-message-${message.type}`} role="status" aria-live="polite">
          {message.text}
        </div>
      )}

      <div className="st-tabs" role="tablist" aria-label={t('strength.title')}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`st-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            role="tab"
            aria-selected={activeTab === tab.id}
          >
            <span aria-hidden="true">{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="st-empty">{t('strength.loading')}</div>
      ) : (
        <>
          {activeTab === 'train' && (
            <TrainView
              {...shared}
              plans={plans}
              stravaStatus={stravaStatus}
              onPlansChanged={loadPlans}
              onStravaChanged={loadStravaStatus}
              onGoToPlans={() => setActiveTab('plans')}
              onGoToStrava={() => setActiveTab('strava')}
              onGoToProgress={() => setActiveTab('progress')}
            />
          )}
          {activeTab === 'plans' && (
            <PlansView
              {...shared}
              plans={plans}
              onPlansChanged={loadPlans}
              onTrainStarted={() => setActiveTab('train')}
            />
          )}
          {activeTab === 'history' && (
            <SessionHistory {...shared} stravaStatus={stravaStatus} onStravaChanged={loadStravaStatus} />
          )}
          {activeTab === 'progress' && <ProgressView {...shared} />}
          {activeTab === 'strava' && (
            <StravaPanel {...shared} status={stravaStatus} onStatusChanged={setStravaStatus} />
          )}
        </>
      )}
    </div>
  );
}

export default StrengthApp;
